import { AnimatePresence, motion, useAnimation } from 'framer-motion';
import { useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { MenuArrowDownSvg } from '../../../assets/icons';
import theme from '../../../assets/theme';
import { useOnOutsideClick } from '../../../hooks/useOnOutsideClick';
import { Button } from '../Button';

const CheckboxIcon = ({ isSelected }: { isSelected: boolean }) => {
  return (
    <CheckContainer $isSelected={isSelected}>
      {isSelected && (
        <svg
          style={{ marginRight: 0 }}
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M4 0C1.79086 0 0 1.79086 0 4V16C0 18.2091 1.79086 20 4 20H16C18.2091 20 20 18.2091 20 16V4C20 1.79086 18.2091 0 16 0H4ZM17.6179 6.50021C18.1703 5.88283 18.1176 4.93454 17.5002 4.38215C16.8828 3.82976 15.9345 3.88244 15.3822 4.49982L7.87909 12.8856L4.49656 9.8789C3.87739 9.32852 2.92928 9.38429 2.3789 10.0035C1.82853 10.6226 1.8843 11.5708 2.50347 12.1211L7.00347 16.1211C7.62122 16.6702 8.56676 16.6162 9.11788 16.0002L17.6179 6.50021Z"
            fill="#61B195"
          />
        </svg>
      )}
    </CheckContainer>
  );
};

const CheckContainer = styled.div<{ $isSelected: boolean }>`
  width: 20px;
  height: 20px;
  border: 1px solid ${theme.colors.grey};
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease-in-out;
  ${({ $isSelected }) =>
    $isSelected &&
    css`
      border: 0px solid ${theme.colors.grey};
    `}

  svg {
    margin-right: 0;
    height: 20px;
    width: 20px;
    transition: all 0.2s ease-in-out;

    ${({ $isSelected }) =>
      !$isSelected &&
      css`
        height: 0px;
        width: 0px;
      `}
  }
`;

const HomepageFilterSelect = ({
  selectedItems,
  selectableItems,
  onSelect,
  label,
  singleSelect,
}: {
  selectedItems: string[];
  selectableItems: { displayName: string; value: string }[];
  onSelect?: (value: string | number | boolean) => void;
  label: string;
  singleSelect: boolean;
}) => {
  const [dropdownIsOpen, setDropdownIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  useOnOutsideClick(dropdownRef, () => setDropdownIsOpen(false));
  const overlayOptions = (
    <DropdownStyleContainer ref={dropdownRef}>
      <div
        style={{
          position: 'relative',
          height: '100%',
          width: '100%',
          overflowY: 'auto',
          maxHeight: 400,
        }}
      >
        {selectableItems.map(option => {
          const isSelected = selectedItems?.some(item => item === option.value);
          return (
            <OptionButton
              styles={{
                width: '100%',
                border: 'none',
                boxShadow: 'none',
                justifyContent: 'space-between',
              }}
              $isSelected={isSelected}
              $singleSelect={singleSelect}
              type="ghost"
              key={option.value}
              onClick={() => {
                singleSelect && setDropdownIsOpen(false);
                onSelect && onSelect(option.value);
              }}
              icon={singleSelect ? undefined : <CheckboxIcon isSelected={!!isSelected} />}
            >
              {option.displayName}
            </OptionButton>
          );
        })}
      </div>
      {!singleSelect && (
        <Button
          styles={{ width: 'calc(100% - 10px)', margin: '10px 5px 5px 5px' }}
          onClick={() => setDropdownIsOpen(false)}
        >
          Done
        </Button>
      )}
    </DropdownStyleContainer>
  );

  const animation = useAnimation();
  const hoverSequence = async () => {
    await animation.start({ scale: 1.03, transition: { duration: 0.1 } });
    animation.start({ scale: 1, transition: { duration: 0.1 } });
  };

  const selectedItem = useMemo(() => {
    return selectableItems.find(item => item.value === selectedItems[0]);
  }, [selectedItems, selectableItems]);

  return (
    <div style={{ position: 'relative' }}>
      {!singleSelect && selectedItems.length >= 1 && (
        <SelectedCount>
          <span>{selectedItems.length}</span>
        </SelectedCount>
      )}
      <AnimatePresence>
        <motion.div
          animate={animation}
          onHoverStart={hoverSequence}
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <MainButton
            type="ghost"
            icon={<MenuArrowDownSvg />}
            onClick={() => setDropdownIsOpen(!dropdownIsOpen)}
          >
            {singleSelect && selectedItem ? selectedItem.displayName : label}
          </MainButton>
        </motion.div>
      </AnimatePresence>
      {dropdownIsOpen && overlayOptions}
    </div>
  );
};

const MainButton = styled(Button)`
  border: none;
  box-shadow: none;
  padding: 5px 8px;
  font-size: 14px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: row-reverse;
  background: none;
  span {
    display: flex;
    font-size: 16px;
    font-weight: 600;
    color: ${theme.colors.white};
  }
  svg {
    height: 21px;
    width: 11px;
    margin-left: 10px;
    color: ${theme.colors.white};
  }

  &:hover,
  &:focus {
    background: none;
  }
`;

const SelectedCount = styled.div`
  position: absolute;
  top: -5px;
  right: -5px;
  background: ${theme.colors.translucentBlack};
  color: ${theme.colors.white};
  height: 15px;
  width: 15px;
  border-radius: 10px;
  font-size: 10px;
  display: flex;
  justify-content: center;
  z-index: 2;

  span {
    margin: 1px;
    display: inline;
  }
`;

const OptionButton = styled(Button)<{ $isSelected?: boolean; $singleSelect: boolean }>`
  border: none;
  box-shadow: none;
  padding: 10px 8px;
  font-size: 14px;
  border: none;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: row-reverse;
  span {
    display: flex;
    font-size: 16px;
    font-weight: 600;
  }
  &:hover {
    background-color: ${theme.colors.grey};
  }
  ${({ $isSelected }) =>
    $isSelected &&
    css`
      &:hover {
        background-color: ${theme.colors.grey};
      }
    `}
  ${({ $singleSelect, $isSelected }) =>
    $singleSelect &&
    $isSelected &&
    css`
      background-color: ${theme.colors.green}40;
    `}
  ${({ $singleSelect }) =>
    $singleSelect &&
    css`
      justify-content: flex-start;
      flex-direction: row;
    `}
`;

const DropdownStyleContainer = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  width: 260px;
  z-index: 100;
  border-radius: 4px;
  background-color: ${theme.colors.white};
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.25);
`;

export default HomepageFilterSelect;
